import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Client } from '../../../../../projects/kiene-core/src/lib/components/client/classes/client';
import { GruppeTier } from '../../../../../projects/kiene-core/src/lib/components/gruppe-tier/classes/gruppe-tier';
import { Tierarzt } from '../../../../../projects/kiene-core/src/lib/components/tieraerzte/classes/tierarzt';
import { Signature } from '../../../../../projects/kiene-core/src/lib/components/unterschriften/signature';
import {
    API_BASE_URL_SERVICE,
    SIGNATURE_URL_SERVICE,
} from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { Dokument } from '../dokument';
import { NotschlachtVorlagePreviewDialogComponent } from './vorlage-preview-dialog.component';

interface NotschlachtTierart {
    tierart_id: number;
    tierart: string;
    mnp_rind?: number;
    mnp_schwein?: number;
}

export interface CreateNotschlachtDialogData {
    kunde: Client;
    tierarzt_id: number;
    /** Admin-Bearbeitung: bestehendes Dokument (freitext/nutzungsart vorbefüllen) */
    dokument?: Dokument & {
        freitext?: string;
        nutzungsart?: string;
        vereinbarungen?: string;
        ort?: string;
        datum?: string | Date;
        kuid?: string;
    };
}

@Component({
    selector: 'kiene-create-notschlachtbescheinigung-dialog',
    templateUrl: './create-notschlachtbescheinigung-dialog.component.html',
    styleUrls: ['./create-notschlachtbescheinigung-dialog.component.scss'],
})
export class CreateNotschlachtbescheinigungDialogComponent implements OnInit {
    vorlagen = [
        { key: 'westmuensterland', label: 'Westmünsterland Kreis Borken' },
        { key: 'kreis_wesel', label: 'Kreis Wesel' },
        { key: 'kreis_steinfurt', label: 'Kreis Steinfurt' },
        { key: 'kreis_guetersloh', label: 'Kreis Gütersloh' },
        { key: 'fischer', label: 'Fischer GmbH' },
        { key: 'diekmann', label: 'Diekmann GmbH (Herbergen)' },
        { key: 'diekmann_friesoythe', label: 'Diekmann Friesoythe' },
        { key: 'schwarting', label: 'Schwarting (Ganderkesee)' },
        { key: 'rolfes', label: 'Rolfes' },
        { key: 'hohn', label: 'Schlachterei G. Hohn GmbH' },
        { key: 'neutral', label: 'Neutral (ohne Schlachter-Logo)' },
        { key: 'westphal', label: 'Andreas Westphal (Landkreis Harburg)' },
    ];

    schlachtbetriebDefault: Record<string, string> = {
        diekmann: 'Schlachterei Gerhard Diekmann GmbH, Lastruper Str. 4, 49632 Essen-HERBERGEN',
        diekmann_friesoythe: 'Schlachterei Diekmann, Friesoythe',
        fischer: 'Fischer GmbH, Zeppelinstraße 17, 49733 Haren',
        schwarting: 'Schwarting, Weserstrasse 2, 27777 Ganderkesee',
        hohn: 'G. Hohn, Gildestr. 6, 26939 Großenmeer',
        westmuensterland: '',
        kreis_wesel: '',
        kreis_steinfurt: '',
        kreis_guetersloh: '',
        rolfes: 'A. Rolfes GmbH, Norderstraße 52, 26847 Detern (mobile Einheit LER-AN 326)',
        neutral: '',
        westphal: 'Andreas Westphal, Bruchweg 5, 21258 Heidenau',
    };

    /** Vorlagen mit Begleitschein-Zusatzfeldern (Rasse, Vitalwerte, …) */
    private begleitVorlagen = new Set([
        'diekmann',
        'diekmann_friesoythe',
        'schwarting',
        'rolfes',
        'hohn',
        'neutral',
        'westphal',
    ]);

    form = new UntypedFormGroup({
        vorlage: new UntypedFormControl('westmuensterland', Validators.required),
        tier_modus: new UntypedFormControl('bestand'),
        gt_id: new UntypedFormControl(null),
        kennzeichnung: new UntypedFormControl(''),
        tierart: new UntypedFormControl(''),
        tierart_id: new UntypedFormControl(null),
        anzahl_tiere: new UntypedFormControl(1),
        amtl_nr: new UntypedFormControl(''),
        transportmittel: new UntypedFormControl(''),
        schlachtbetrieb: new UntypedFormControl(''),
        grund: new UntypedFormControl(''),
        gesundheit: new UntypedFormControl(''),
        behandlung: new UntypedFormControl(''),
        sonstige: new UntypedFormControl(''),
        datum_untersuchung: new UntypedFormControl(new Date()),
        uhrzeit_untersuchung: new UntypedFormControl(this.nowTime()),
        datum_schlachtung: new UntypedFormControl(new Date()),
        uhrzeit_schlachtung: new UntypedFormControl(this.nowTime()),
        ort: new UntypedFormControl(''),
        datum: new UntypedFormControl(new Date()),
        // Diekmann
        rasse: new UntypedFormControl(''),
        geschlecht: new UntypedFormControl(''),
        alter: new UntypedFormControl(''),
        kenn_ohrmarken: new UntypedFormControl(true),
        kenn_chip: new UntypedFormControl(false),
        kenn_equidenpass: new UntypedFormControl(false),
        kenn_taetowierung: new UntypedFormControl(false),
        taetowierung_nr: new UntypedFormControl(''),
        temperatur: new UntypedFormControl(''),
        herzfrequenz: new UntypedFormControl(''),
        atemfrequenz: new UntypedFormControl(''),
        sonstige_befunde: new UntypedFormControl(''),
        behandlung_ja: new UntypedFormControl(false),
        behandlung_nein: new UntypedFormControl(true),
        kfz_clp_dh_43: new UntypedFormControl(false),
        kfz_clp_dh_36: new UntypedFormControl(false),
        kfz_nummer: new UntypedFormControl(''),
        arzneimittel_ja: new UntypedFormControl(false),
        arzneimittel_nein: new UntypedFormControl(true),
        arzneimittel_angabe: new UntypedFormControl(''),
        wartezeit: new UntypedFormControl(''),
        behandlungsdaten: new UntypedFormControl(''),
        fleisch_ja: new UntypedFormControl(true),
        fleisch_nein: new UntypedFormControl(false),
        fleisch_nein_plausibel: new UntypedFormControl(false),
        fleisch_nein_untauglich: new UntypedFormControl(false),
        fleisch_befunde: new UntypedFormControl(''),
        // Steinfurt / Schwarting: getrennte Adressfelder
        ns_strasse: new UntypedFormControl(''),
        ns_hausnummer: new UntypedFormControl(''),
        ns_plz: new UntypedFormControl(''),
        ns_ort: new UntypedFormControl(''),
        sb_strasse: new UntypedFormControl(''),
        sb_hausnummer: new UntypedFormControl(''),
        sb_plz: new UntypedFormControl(''),
        sb_ort: new UntypedFormControl(''),
        optional: new UntypedFormControl(''),
    });

    tierParams: HttpParams;
    historieDurchsuchen = false;
    /** Remount-Key für Autocomplete bei Historie-Wechsel */
    autocompleteReady = true;
    selectedTier: GruppeTier;
    tierarten: NotschlachtTierart[] = [];
    signature = new Signature();
    qrcodeUrl: string;
    isSaving = false;
    tierarztGesetzt = false;
    isEditMode = false;

    constructor(
        public dialogRef: MatDialogRef<CreateNotschlachtbescheinigungDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CreateNotschlachtDialogData,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        @Inject(SIGNATURE_URL_SERVICE) private signatureUrl: string,
        private api: KieneTableService,
        private notification: MessageService,
        private pdfService: PdfViewerDesktopService,
        private dialog: MatDialog,
    ) {
        this.isEditMode = !!data.dokument?.dokument_id;
        this.signature.kunde_id = data.kunde?.kunde_id;
        this.signature.tierarzt_id = data.dokument?.tierarzt_id || data.tierarzt_id;
        this.tierarztGesetzt = !!this.signature.tierarzt_id;
        this.tierParams = this.buildTierParams(false);
    }

    ngOnInit() {
        const kundeId = Number(this.data.kunde?.kunde_id);
        if (!kundeId) {
            this.notification.alertMessage('Bitte zuerst einen Betrieb auswählen.');
            this.dialogRef.close(null);
            return;
        }

        const k = this.data.kunde;
        const strasseRaw = (k.strasse_final || k.strasse || '').trim();
        let nsStrasse = strasseRaw;
        let nsHnr = '';
        const hnrMatch = strasseRaw.match(/^(.*)\s+(\d+\s*[a-zA-Z]?)$/);
        if (hnrMatch) {
            nsStrasse = hnrMatch[1].trim();
            nsHnr = hnrMatch[2].trim();
        }
        this.form.patchValue({
            ort: k.ort_final || k.ort || '',
            schlachtbetrieb: this.schlachtbetriebDefault['westmuensterland'],
            ns_strasse: nsStrasse,
            ns_hausnummer: nsHnr,
            ns_plz: k.plz_final || k.plz || '',
            ns_ort: k.ort_final || k.ort || '',
        });

        this.form.get('vorlage').valueChanges.subscribe((vorlage: string) => {
            const current = this.form.get('schlachtbetrieb').value;
            const defaults = Object.values(this.schlachtbetriebDefault);
            if (!current || defaults.includes(current)) {
                this.form.patchValue({ schlachtbetrieb: this.schlachtbetriebDefault[vorlage] || '' });
            }
            // CLP-DH nur Herbergen – nie in andere Vorlagen mitnehmen
            if (vorlage !== 'diekmann') {
                this.form.patchValue({ kfz_clp_dh_43: false, kfz_clp_dh_36: false });
            }
            if (
                vorlage === 'neutral' ||
                vorlage === 'hohn' ||
                vorlage === 'diekmann_friesoythe' ||
                vorlage === 'schwarting' ||
                vorlage === 'westphal'
            ) {
                this.form.patchValue({ fleisch_ja: false, fleisch_nein: false, fleisch_befunde: '' });
            }
            if (this.selectedTier) {
                const alterText = this.formatAlterFromTage(
                    Number((this.selectedTier as any)?.alter_tage),
                    vorlage === 'neutral' || vorlage === 'hohn',
                );
                if (alterText) {
                    this.form.patchValue({ alter: alterText });
                }
            }
        });

        this.form.get('tier_modus').valueChanges.subscribe(() => {
            this.selectedTier = undefined;
            this.form.patchValue({ gt_id: null, kennzeichnung: '' });
        });

        this.form.get('tierart_id').valueChanges.subscribe((tierartId: number) => {
            const t = this.tierarten.find(x => Number(x.tierart_id) === Number(tierartId));
            this.form.patchValue({ tierart: t?.tierart || '' }, { emitEvent: false });
        });

        this.ladeTierarten();
        if (this.isEditMode) {
            this.applyStoredDokument();
        } else {
            this.prepareSigning();
        }
    }

    get vorlagePreviewUrl(): string {
        const key = this.form.get('vorlage')?.value || 'westmuensterland';
        return `${this.apiBaseUrl}beleg/vorlagen/notschlacht/${key}/page_1.png`;
    }

    openVorlagePreview() {
        const key = this.form.get('vorlage')?.value || 'westmuensterland';
        const label = this.vorlagen.find(v => v.key === key)?.label || 'Vorlage';
        this.dialog.open(NotschlachtVorlagePreviewDialogComponent, {
            width: '720px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data: {
                title: `Vorschau: ${label}`,
                imageUrl: this.vorlagePreviewUrl,
            },
        });
    }

    private buildTierParams(historie: boolean): HttpParams {
        const kundeId = Number(this.data.kunde?.kunde_id) || 0;
        return new HttpParams()
            .set('kunde_id', String(kundeId))
            .set('historie_durchsuchen', historie ? '1' : '0')
            .set('tot', historie ? '1' : '0');
    }

    private applyStoredDokument() {
        const dok = this.data.dokument;
        if (!dok) {
            return;
        }
        let stored: Record<string, any> = {};
        if (dok.freitext) {
            try {
                const parsed = JSON.parse(dok.freitext);
                if (parsed && typeof parsed === 'object') {
                    stored = parsed;
                }
            } catch {
                /* freitext war kein JSON */
            }
        }
        const vorlage = stored.vorlage || dok.nutzungsart || 'westmuensterland';
        const kennzeichnung = stored.kennzeichnung || dok.vereinbarungen || '';
        const toDate = (v: any): Date | null => {
            if (!v) {
                return null;
            }
            const d = v instanceof Date ? v : new Date(v);
            return isNaN(d.getTime()) ? null : d;
        };
        const toBool = (v: any, fallback = false) => {
            if (v === undefined || v === null || v === '') {
                return fallback;
            }
            return Number(v) === 1 || v === true || v === 'true';
        };

        this.form.patchValue({
            vorlage,
            tier_modus: stored.gt_id || kennzeichnung ? (stored.gt_id ? 'bestand' : 'manuell') : 'manuell',
            gt_id: stored.gt_id || null,
            kennzeichnung,
            tierart: stored.tierart || '',
            tierart_id: stored.tierart_id || null,
            anzahl_tiere: stored.anzahl_tiere || 1,
            amtl_nr: stored.amtl_nr || '',
            transportmittel: stored.transportmittel || '',
            schlachtbetrieb: stored.schlachtbetrieb || this.schlachtbetriebDefault[vorlage] || '',
            grund: stored.grund || '',
            gesundheit: stored.gesundheit || '',
            behandlung: stored.behandlung || '',
            sonstige: stored.sonstige || '',
            optional: stored.optional || '',
            datum_untersuchung: toDate(stored.datum_untersuchung) || new Date(),
            uhrzeit_untersuchung: stored.uhrzeit_untersuchung || this.nowTime(),
            datum_schlachtung: toDate(stored.datum_schlachtung) || new Date(),
            uhrzeit_schlachtung: stored.uhrzeit_schlachtung || this.nowTime(),
            ort: stored.ort || dok.ort || this.form.get('ort').value,
            datum: toDate(stored.datum) || toDate(dok.datum) || new Date(),
            rasse: stored.rasse || '',
            geschlecht: stored.geschlecht || '',
            alter: stored.alter || '',
            kenn_ohrmarken: toBool(stored.kenn_ohrmarken, true),
            kenn_chip: toBool(stored.kenn_chip),
            kenn_equidenpass: toBool(stored.kenn_equidenpass),
            kenn_taetowierung: toBool(stored.kenn_taetowierung),
            taetowierung_nr: stored.taetowierung_nr || '',
            temperatur: stored.temperatur || '',
            herzfrequenz: stored.herzfrequenz || '',
            atemfrequenz: stored.atemfrequenz || '',
            sonstige_befunde: stored.sonstige_befunde || '',
            behandlung_ja: toBool(stored.behandlung_ja),
            behandlung_nein: toBool(stored.behandlung_nein, true),
            kfz_clp_dh_43: toBool(stored.kfz_clp_dh_43),
            kfz_clp_dh_36: toBool(stored.kfz_clp_dh_36),
            kfz_nummer: stored.kfz_nummer || '',
            arzneimittel_ja: toBool(stored.arzneimittel_ja),
            arzneimittel_nein: toBool(stored.arzneimittel_nein, true),
            arzneimittel_angabe: stored.arzneimittel_angabe || '',
            wartezeit: stored.wartezeit || '',
            behandlungsdaten: stored.behandlungsdaten || '',
            fleisch_ja: false,
            fleisch_nein: false,
            fleisch_nein_plausibel: toBool(stored.fleisch_nein_plausibel),
            fleisch_nein_untauglich: toBool(stored.fleisch_nein_untauglich),
            fleisch_befunde: stored.fleisch_befunde || '',
            ns_strasse: stored.ns_strasse || '',
            ns_hausnummer: stored.ns_hausnummer || '',
            ns_plz: stored.ns_plz || '',
            ns_ort: stored.ns_ort || '',
        });

        this.signature.kuid = dok.kuid || null;
        this.signature.tierarzt_id = dok.tierarzt_id || this.signature.tierarzt_id;
        this.tierarztGesetzt = !!this.signature.tierarzt_id;
    }

    private ladeTierarten() {
        const kundeId = this.data.kunde?.kunde_id;
        if (!kundeId) {
            this.ladeAlleTierarten();
            return;
        }
        const params = new HttpParams().set('kunde_id', String(kundeId));
        this.api.get(this.apiBaseUrl + 'kunde_tierart/read.php', params).subscribe({
            next: response => {
                const records = (response?.records || []) as NotschlachtTierart[];
                if (records.length > 0) {
                    this.tierarten = records;
                    this.preselectTierart();
                } else {
                    this.ladeAlleTierarten();
                }
            },
            error: () => {
                this.ladeAlleTierarten();
            },
        });
    }

    private ladeAlleTierarten() {
        this.api.get(this.apiBaseUrl + 'tierart/read.php').subscribe({
            next: response => {
                this.tierarten = (response?.records || []) as NotschlachtTierart[];
                this.preselectTierart();
            },
            error: err => {
                this.notification.errorMessage('Tierarten konnten nicht geladen werden!', err);
            },
        });
    }

    private preselectTierart() {
        if (!this.tierarten?.length) {
            return;
        }
        if (this.form.get('tierart_id').value) {
            return;
        }
        const k = this.data.kunde;
        let pick: NotschlachtTierart;
        if (this.tierarten.length === 1) {
            pick = this.tierarten[0];
        } else if (k?.rind) {
            pick = this.tierarten.find(t => Number(t.mnp_rind) === 1) || this.tierarten[0];
        } else if (k?.schwein) {
            pick = this.tierarten.find(t => Number(t.mnp_schwein) === 1) || this.tierarten[0];
        } else {
            pick = this.tierarten[0];
        }
        if (pick) {
            this.form.patchValue({
                tierart_id: pick.tierart_id,
                tierart: pick.tierart,
            });
        }
    }

    get isDiekmann(): boolean {
        return this.begleitVorlagen.has(this.form.get('vorlage').value);
    }

    /** Nur Herbergen: feste Kfz CLP-DH 43/36 */
    get isDiekmannHerbergen(): boolean {
        return this.form.get('vorlage').value === 'diekmann';
    }

    /** Friesoythe: nur Ohrmarken/Tätowierung, keine Plausibel-/Untauglich-Kästen */
    get isDiekmannFriesoythe(): boolean {
        return this.form.get('vorlage').value === 'diekmann_friesoythe';
    }

    get isSchwarting(): boolean {
        return this.form.get('vorlage').value === 'schwarting';
    }

    get isRolfes(): boolean {
        return this.form.get('vorlage').value === 'rolfes';
    }

    get isHohn(): boolean {
        return this.form.get('vorlage').value === 'hohn';
    }

    get isNeutral(): boolean {
        return this.form.get('vorlage').value === 'neutral';
    }

    get isWestphal(): boolean {
        return this.form.get('vorlage').value === 'westphal';
    }

    /** Kompakte Begleitscheine ohne Kreis-Extrafelder */
    get hideKreisExtraFields(): boolean {
        return this.isDiekmannFriesoythe || this.isSchwarting || this.isRolfes || this.isHohn || this.isNeutral;
    }

    /** Arzneimittel/Fleisch: Rolfes hat das nicht auf dem 1-Seiten-Formular */
    get showBegleitArzneimittelFleisch(): boolean {
        return this.isDiekmann && !this.isRolfes;
    }

    /** Eine Kennzeichnungs-Nr.-Zeile statt Checkboxen */
    get hideKennCheckboxes(): boolean {
        return this.isSchwarting || this.isHohn || this.isNeutral || this.isWestphal;
    }

    get isSteinfurt(): boolean {
        return this.form.get('vorlage').value === 'kreis_steinfurt';
    }

    get isFischer(): boolean {
        return this.form.get('vorlage').value === 'fischer';
    }

    get isManuell(): boolean {
        return this.form.get('tier_modus').value === 'manuell';
    }

    onBehandlungJa(checked: boolean) {
        if (checked) {
            this.form.patchValue({ behandlung_nein: false });
        }
    }

    onBehandlungNein(checked: boolean) {
        if (checked) {
            this.form.patchValue({ behandlung_ja: false, behandlung: '' });
        }
    }

    onArzneimittelJa(checked: boolean) {
        if (checked) {
            this.form.patchValue({ arzneimittel_nein: false });
        }
    }

    onArzneimittelNein(checked: boolean) {
        if (checked) {
            this.form.patchValue({
                arzneimittel_ja: false,
                arzneimittel_angabe: '',
                wartezeit: '',
                behandlungsdaten: '',
            });
        }
    }

    onFleischJa(checked: boolean) {
        if (checked) {
            this.form.patchValue({ fleisch_nein: false, fleisch_befunde: '' });
        }
    }

    onFleischNein(checked: boolean) {
        if (checked) {
            this.form.patchValue({ fleisch_ja: false });
        }
    }

    nowTime(): string {
        const d = new Date();
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }

    toggleHistorie(checked: boolean) {
        this.historieDurchsuchen = checked;
        this.tierParams = this.buildTierParams(checked);
        // Autocomplete remounten, damit neue Params sicher greifen
        this.autocompleteReady = false;
        setTimeout(() => {
            this.autocompleteReady = true;
        }, 0);
    }

    private formatAlterFromTage(alterTage: number, inMonaten: boolean): string {
        if (Number.isNaN(alterTage) || alterTage < 0) {
            return '';
        }
        if (inMonaten) {
            return `${Math.round(alterTage / 30.437)} M`;
        }
        if (alterTage >= 365) {
            return `${Math.floor(alterTage / 365)} J`;
        }
        if (alterTage >= 30) {
            return `${Math.floor(alterTage / 30)} Mon.`;
        }
        return `${alterTage} Tage`;
    }

    onTierSelected(tier: GruppeTier) {
        this.selectedTier = tier;
        const alterText = this.formatAlterFromTage(Number((tier as any)?.alter_tage), this.isNeutral || this.isHohn);
        const geschlechtRaw = String(tier?.geschlecht || '').toLowerCase();
        let geschlecht = '';
        if (geschlechtRaw.includes('weib') || geschlechtRaw === 'w' || geschlechtRaw === 'f') {
            geschlecht = 'w';
        } else if (
            geschlechtRaw.includes('männ') ||
            geschlechtRaw.includes('maenn') ||
            geschlechtRaw === 'm'
        ) {
            geschlecht = 'm';
        } else if (tier?.geschlecht_id === 2) {
            geschlecht = 'w';
        } else if (tier?.geschlecht_id === 1) {
            geschlecht = 'm';
        } else if (geschlechtRaw) {
            geschlecht = geschlechtRaw.charAt(0);
        }
        const patch: Record<string, unknown> = {
            gt_id: tier?.gt_id || null,
            kennzeichnung: tier?.ohrmarke_notnull || tier?.ohrmarke || '',
            kenn_ohrmarken: true,
        };
        const rasse = tier?.rasse_kuerzel || tier?.rasse || '';
        if (rasse) {
            patch.rasse = rasse;
        }
        if (geschlecht) {
            patch.geschlecht = geschlecht;
        }
        if (alterText) {
            patch.alter = alterText;
        }
        this.form.patchValue(patch);
    }

    onTierartSelect(tierartId: number) {
        const t = this.tierarten.find(x => Number(x.tierart_id) === Number(tierartId));
        this.form.patchValue({
            tierart_id: tierartId || null,
            tierart: t?.tierart || '',
        });
    }

    setSelectedTierarzt(tierarzt: Tierarzt) {
        this.signature.tierarzt_id = tierarzt.tierarzt_id;
        this.prepareSigning();
    }

    prepareSigning() {
        this.signature.kuid = null;
        this.api.post(this.apiBaseUrl + 'unterschrift/create.php', this.signature).subscribe({
            next: response => {
                this.signature = response;
                this.qrcodeUrl = this.signatureUrl + 'sign/' + this.signature.kuid;
            },
            error: error => {
                if (error.status === 400) {
                    this.notification.alertMessage(error.error.errortext);
                } else {
                    this.notification.errorMessage('Es ist ein Fehler aufgetreten: ', error);
                }
                this.dialogRef.close(null);
            },
        });
    }

    createPdf() {
        if (this.isManuell && !this.form.get('kennzeichnung').value) {
            this.notification.alertMessage('Bitte Kennzeichnung/Ohrmarke eintragen.');
            return;
        }
        if (this.isManuell && !this.form.get('tierart').value) {
            this.notification.alertMessage('Bitte eine Tierart auswaehlen.');
            return;
        }
        if (!this.isManuell && !this.form.get('kennzeichnung').value && !this.selectedTier) {
            this.notification.alertMessage('Bitte ein Tier auswaehlen oder manuell eintragen.');
            return;
        }
        if (!this.signature.tierarzt_id) {
            this.notification.alertMessage('Bitte einen Tierarzt auswaehlen.');
            return;
        }

        this.isSaving = true;
        const raw = this.form.getRawValue();
        const k = this.data.kunde;
        const payload: any = {
            ...raw,
            kunde_id: this.signature.kunde_id,
            tierarzt_id: this.signature.tierarzt_id,
            kuid: this.signature.kuid,
            eigentuemer: k.bezeichnung_final || k.bezeichnung || '',
            anschrift: `${k.strasse_final || k.strasse || ''}, ${k.plz_final || k.plz || ''} ${
                k.ort_final || k.ort || ''
            }`.trim(),
            vvvo: k.vvvo || '',
            kenn_ohrmarken: raw.kenn_ohrmarken ? 1 : 0,
            kenn_chip: raw.kenn_chip ? 1 : 0,
            kenn_equidenpass: raw.kenn_equidenpass ? 1 : 0,
            kenn_taetowierung: raw.kenn_taetowierung ? 1 : 0,
            kfz_clp_dh_43: raw.kfz_clp_dh_43 ? 1 : 0,
            kfz_clp_dh_36: raw.kfz_clp_dh_36 ? 1 : 0,
            behandlung_ja: raw.behandlung_ja ? 1 : 0,
            behandlung_nein: raw.behandlung_nein ? 1 : 0,
            arzneimittel_ja: raw.arzneimittel_ja ? 1 : 0,
            arzneimittel_nein: raw.arzneimittel_nein ? 1 : 0,
            fleisch_ja: 0,
            fleisch_nein: 0,
            fleisch_nein_plausibel: raw.fleisch_nein_plausibel ? 1 : 0,
            fleisch_nein_untauglich: raw.fleisch_nein_untauglich ? 1 : 0,
        };

        const params = new HttpParams().append('kunde_id', String(this.signature.kunde_id));
        const endpoint = this.isEditMode
            ? 'beleg/pdf_notschlachtbescheinigung_update.php'
            : 'beleg/pdf_notschlachtbescheinigung.php';
        if (this.isEditMode) {
            payload.dokument_id = this.data.dokument.dokument_id;
        }

        this.api.post(this.apiBaseUrl + endpoint, payload, params).subscribe({
            next: (dokument: Dokument) => {
                const dokId = dokument?.dokument_id || this.data.dokument?.dokument_id;
                const input = new PdfViewerDialogInput(
                    'dokument/read_or_mail_pdf.php',
                    new HttpParams().set('dokument_id', dokId?.toString()),
                    'Notschlachtbescheinigung',
                );
                this.pdfService.openPdfViewer(input);
                this.isSaving = false;
                this.dialogRef.close(dokument);
            },
            error: error => {
                this.notification.errorMessage('', error);
                this.isSaving = false;
            },
        });
    }

    cancel() {
        this.dialogRef.close(null);
    }
}
